import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { foodItemAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ImageUpload } from '../../components/ui/image-upload';
import { toast } from 'sonner';
import { ArrowLeft, Loader2 } from 'lucide-react';
import SellerLayout from '../../components/layouts/SellerLayout';

const CATEGORIES = [
  'Breakfast',
  'Lunch',
  'Dinner',
  'Snacks',
  'Sweets',
  'Beverages',
  'Pickles & Chutneys',
  'Homemade Specials',
  'Festival Specials',
  'Other'
];

const MEAL_TYPES = [
  { value: 'breakfast', label: 'Breakfast' },
  { value: 'lunch', label: 'Lunch' },
  { value: 'dinner', label: 'Dinner' },
  { value: 'snacks', label: 'Snacks' },
  { value: 'beverages', label: 'Beverages' },
  { value: 'all_day', label: 'All Day' },
];

export default function ItemForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [loadingItem, setLoadingItem] = useState(isEdit);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    quantity: '10',
    category: '',
    image_url: '',
    is_vegetarian: true,
    is_free: false,
    meal_type: 'all_day'
  });

  useEffect(() => {
    if (isEdit) {
      loadItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadItem = async () => {
    try {
      const response = await foodItemAPI.getById(id);
      const item = response.data;
      setFormData({
        name: item.name,
        description: item.description,
        price: item.price.toString(),
        quantity: item.quantity.toString(),
        category: item.category,
        image_url: item.image_url || '',
        is_vegetarian: item.is_vegetarian,
        is_free: item.is_free,
        meal_type: item.meal_type || 'all_day'
      });
    } catch (error) {
      toast.error('Failed to load item');
      navigate(-1);
    } finally {
      setLoadingItem(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Please enter item name');
      return;
    }
    if (!formData.category) {
      toast.error('Please select a category');
      return;
    }
    if (!formData.is_free && (!formData.price || parseFloat(formData.price) <= 0)) {
      toast.error('Please enter a valid price');
      return;
    }

    setLoading(true);
    try {
      const itemData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        price: formData.is_free ? 0 : parseFloat(formData.price),
        quantity: parseInt(formData.quantity) || 10,
        category: formData.category,
        image_url: formData.image_url.trim() || null,
        is_vegetarian: formData.is_vegetarian,
        is_free: formData.is_free,
        meal_type: formData.meal_type
      };

      if (isEdit) {
        await foodItemAPI.update(id, itemData);
        toast.success('Item updated successfully');
      } else {
        await foodItemAPI.create({ ...itemData, seller_id: user.id });
        toast.success('Item added successfully');
      }
      
      navigate(user.role === 'dealer' ? '/dealer/items' : '/seller/items');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save item');
    } finally {
      setLoading(false);
    }
  };

  const basePath = user?.role === 'dealer' ? '/dealer' : '/seller';

  if (loadingItem) {
    return (
      <SellerLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </SellerLayout>
    );
  }

  return (
    <SellerLayout>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-muted rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-heading text-2xl font-bold">
            {isEdit ? 'Edit Item' : 'Add New Item'}
          </h1>
        </div>

        <Card>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Item Name */}
              <div className="space-y-2">
                <Label htmlFor="name">Item Name *</Label>
                <Input
                  id="name"
                  data-testid="item-name"
                  placeholder="e.g., Homemade Samosa"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  data-testid="item-description"
                  placeholder="Describe your dish - ingredients, taste, etc."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>

              {/* Category & Meal Type */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger data-testid="item-category">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Meal Type *</Label>
                  <Select 
                    value={formData.meal_type} 
                    onValueChange={(value) => setFormData({ ...formData, meal_type: value })}
                  >
                    <SelectTrigger data-testid="item-meal-type">
                      <SelectValue placeholder="Select meal type" />
                    </SelectTrigger>
                    <SelectContent>
                      {MEAL_TYPES.map((mt) => (
                        <SelectItem key={mt.value} value={mt.value}>{mt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Price & Quantity Row */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="price">Price (₹) {!formData.is_free && '*'}</Label>
                  <Input
                    id="price"
                    data-testid="item-price"
                    type="number"
                    placeholder="0"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    disabled={formData.is_free}
                    min="0"
                    step="0.5"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="quantity">Available Quantity</Label>
                  <Input
                    id="quantity"
                    data-testid="item-quantity"
                    type="number"
                    placeholder="10"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    min="1"
                  />
                </div>
              </div>

              {/* Image Upload */}
              <div className="space-y-2">
                <Label>Food Item Image</Label>
                <ImageUpload
                  value={formData.image_url}
                  onChange={(url) => setFormData({ ...formData, image_url: url })}
                  placeholder="Upload your dish photo"
                  maxSizeMB={5}
                />
                <p className="text-xs text-muted-foreground">
                  Upload a photo of your dish to attract more buyers!
                </p>
              </div>

              {/* Switches Row */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
                  <div>
                    <Label htmlFor="is_veg">Vegetarian</Label>
                    <p className="text-xs text-muted-foreground">Is this item veg?</p>
                  </div>
                  <Switch
                    id="is_veg"
                    data-testid="item-veg"
                    checked={formData.is_vegetarian}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_vegetarian: checked })}
                  />
                </div>
                <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
                  <div>
                    <Label htmlFor="is_free">Free Item</Label>
                    <p className="text-xs text-muted-foreground">Offer for free</p>
                  </div>
                  <Switch
                    id="is_free"
                    data-testid="item-free"
                    checked={formData.is_free}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_free: checked })}
                  />
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                size="lg"
                className="w-full rounded-full"
                disabled={loading}
                data-testid="save-item-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  isEdit ? 'Update Item' : 'Add Item'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </SellerLayout>
  );
}
